import * as XLSX from 'xlsx';
import { saveBlob } from './saveBlob';
import { toXlsxBlob } from './xlsxBlob';

interface ExcelFromDataOptions {
  filename: string;
  sheetName?: string;
}

/**
 * Generates and downloads an Excel file from in-memory data (not DOM).
 * Used when exporting data across multiple pages that aren't all in the DOM.
 */
export async function generateExcelFromData(
  headers: string[],
  rows: string[][],
  options: ExcelFromDataOptions
): Promise<void> {
  const wsData = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Auto-width columns
  const colWidths = headers.map((header, i) => {
    const maxLen = Math.max(
      header.length,
      ...rows.map((r) => (r[i] || '').length)
    );
    return { wch: Math.min(maxLen + 2, 50) };
  });
  ws['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, options.sheetName || 'Dades');
  // `XLSX.writeFile` se guisa la descarga por dentro, y esa muere en el WebView
  // (no interpreta las URL `blob:`). Generamos el Blob y lo entregamos por el
  // punto único, que sí sabe guardar dentro de la app.
  await saveBlob(await toXlsxBlob(wb), options.filename);
}

interface SheetSpec {
  name: string;
  headers: string[];
  rows: string[][];
}

interface MultiSheetOptions {
  filename: string;
}

/**
 * Genera y descarga un Excel con varias hojas (cada una con sus headers
 * + filas). Mismo estilo que `generateExcelFromData` (auto-width por
 * columna, header como primera fila); aceptando N hojas.
 *
 * Casos de uso: reports agregados con varios niveles de datos (e.g.
 * "Resumen por X" + "Detalle por Y") en un mismo libro.
 *
 * Retrocompatible: `generateExcelFromData` (1 hoja) sigue igual; este
 * helper es aditivo, sin tocar consumidores existentes.
 */
export async function generateMultiSheetExcelFromData(
  sheets: SheetSpec[],
  options: MultiSheetOptions,
): Promise<void> {
  const wb = XLSX.utils.book_new();
  for (const sheet of sheets) {
    const ws = XLSX.utils.aoa_to_sheet([sheet.headers, ...sheet.rows]);
    ws['!cols'] = sheet.headers.map((h, i) => ({
      wch: Math.min(
        50,
        Math.max(
          h.length,
          ...sheet.rows.map((r) => (r[i] || '').length),
        ) + 2,
      ),
    }));
    XLSX.utils.book_append_sheet(wb, ws, sheet.name);
  }
  // `XLSX.writeFile` se guisa la descarga por dentro, y esa muere en el WebView
  // (no interpreta las URL `blob:`). Generamos el Blob y lo entregamos por el
  // punto único, que sí sabe guardar dentro de la app.
  await saveBlob(await toXlsxBlob(wb), options.filename);
}
